import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { purgeQueuedAuthTokens } from "@/lib/offline";
import { initFcmForCurrentUser } from "@/lib/fcm";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/loading, false = unauth, obj = auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Fire-and-forget: register this device for push notifications
      initFcmForCurrentUser().catch(() => { /* non-fatal */ });
    } catch {
      localStorage.removeItem("auth_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("auth_token", data.token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    // Register for push right after fresh login
    initFcmForCurrentUser().catch(() => { /* non-fatal */ });
    return data.user;
  }, []);

  const loginWithOtp = useCallback(async (firebaseIdToken) => {
    const { data } = await api.post("/auth/login-with-otp", { firebase_id_token: firebaseIdToken });
    localStorage.setItem("auth_token", data.token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    initFcmForCurrentUser().catch(() => { /* non-fatal */ });
    return data.user;
  }, []);

  const logout = useCallback(() => {
    const rt = localStorage.getItem("refresh_token");
    // Fire-and-forget revocation — we don't wait for the server so the UI feels instant.
    if (rt) {
      api.post("/auth/logout", { refresh_token: rt }).catch(() => { /* ignore */ });
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    // Best-effort strip any auth tokens we snapshotted into offline-queue items
    // so they can't be replayed after sign-out (Security audit P3).
    purgeQueuedAuthTokens().catch(() => { /* IDB may be unavailable — non-blocking */ });
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, loginWithOtp, logout, refresh }),
    [user, loading, login, loginWithOtp, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
